// src/hooks/useBusinessData.js - Complete File with Debug Logging
import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import { keepLastGood } from './businessDataShape';

const API_BASE = process.env.REACT_APP_API_URL || '';

export const useBusinessData = () => {
  const [businessData, setBusinessData] = useState({
    employees: [], // Default to empty array
    clients: [],   // Default to empty array
    sites: [],     // Default to empty array
    companyInfo: {},
    platformModules: [],
    realTimeMetrics: {},
    financialAnalysis: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // SAFE: Helper function to ensure arrays exist
  const safeArrayFilter = (array, filterFn) => {
    return Array.isArray(array) ? array.filter(filterFn) : [];
  };

  const fetchBusinessData = useCallback(async ({ silent = false } = {}) => {
    try {
      // A background auto-refresh shouldn't flash the full-page loading spinner
      // over live data the user is already looking at - only the initial load
      // (and an explicit manual refresh) should do that.
      if (!silent) setIsLoading(true);
      setError(null);
      const response = await apiService.request('/api/business-data');
      if (response.success) {

        // SAFE: Ensure all arrays exist with fallbacks
        const safeData = {
          employees: Array.isArray(response.data?.employees) ? response.data.employees : [],
          clients: Array.isArray(response.data?.clients) ? response.data.clients : [],
          sites: Array.isArray(response.data?.sites) ? response.data.sites : [],
          companyInfo: response.data?.companyInfo || {},
          platformModules: Array.isArray(response.data?.platformModules) ? response.data.platformModules : [],
          realTimeMetrics: response.data?.realTimeMetrics || {},
          // Revenue/labor-cost math comes from businessService.getFinancialAnalysis on the
          // backend (zero-safe hourly-rate handling) - not recomputed here.
          financialAnalysis: response.data?.financialAnalysis || null,
          summary: response.summary || {},
          // The server scopes this response by role (backend services/accessScope.js):
          // 'full' for manager and above; 'self_site' (own record, own site(s), shifts there)
          // for employees; 'totals' (company aggregates only) for the analyst.
          view: response.view || 'full',
          self: response.data?.self || null,
          shifts: Array.isArray(response.data?.shifts) ? response.data.shifts : [],
          totals: response.data?.totals || null
        };
        
        setBusinessData(safeData);
        console.log('Business data loaded safely:', {
          employees: safeData.employees.length,
          clients: safeData.clients.length,
          sites: safeData.sites.length,
          revenue: safeData.clients.reduce((sum, c) => sum + (c.monthly_revenue || 0), 0)
        });
      } else {
        throw new Error(response.error || 'Failed to fetch business data');
      }
    } catch (err) {
      console.error('Business data fetch error:', err);
      setError(err.message);
      // SAFE: Keep existing data structure even on error
      // ...including the role-scoped fields (view/self/shifts/totals)
      setBusinessData(prev => keepLastGood(prev));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBusinessData();
  }, [fetchBusinessData]);

  // Auto-refresh: this used to fetch once on mount and then sit on whatever it
  // got forever - a transient backend hiccup, an expired session, or just data
  // changing elsewhere left the dashboard showing stale numbers with no way to
  // recover short of a manual page reload. Poll in the background (silent - no
  // spinner flash), and also refetch immediately the moment the tab regains
  // focus or the browser comes back online, so a dropped connection heals
  // itself as soon as it can rather than waiting out the full interval.
  useEffect(() => {
    const POLL_INTERVAL_MS = 30000;
    const interval = setInterval(() => fetchBusinessData({ silent: true }), POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchBusinessData({ silent: true });
    };
    const handleOnline = () => fetchBusinessData({ silent: true });

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchBusinessData]);

  // SAFE: Derived metrics with array safety checks
  const derivedMetrics = {
    activeEmployees: safeArrayFilter(businessData.employees, e => e.status === 'Active'),
    totalClients: businessData.clients?.length || 0,
    activeGuards: safeArrayFilter(businessData.employees, e => 
      e.status === 'Active' && (e.role || '').toLowerCase().includes('guard')
    ),
    supervisors: safeArrayFilter(businessData.employees, e => 
      e.status === 'Active' && (e.role || '').toLowerCase().includes('supervisor')
    ),
    monthlyRevenue: Array.isArray(businessData.clients)
      ? businessData.clients.reduce((sum, client) => sum + (client.monthly_revenue || 0), 0)
      : 0,
    totalModules: businessData.platformModules?.length || 3
  };

  // Create analytics structure that dashboard components expect
  const analyticsData = {
    ...businessData,
    analytics: {
      monthlyRevenue: derivedMetrics.monthlyRevenue,
      totalRevenue: derivedMetrics.monthlyRevenue,
      totalClients: derivedMetrics.totalClients,
      totalSites: businessData.sites?.length || derivedMetrics.totalClients,
      activeEmployees: derivedMetrics.activeEmployees.length,
      
      // DEBUG: Log employee statuses before calculating employeesOnLeave
      employeesOnLeave: (() => {
        console.log('Employee statuses:', businessData.employees.map(e => `${e.name}: ${e.status}`));
        const onLeaveEmployees = safeArrayFilter(businessData.employees, e => e.status === 'On Leave');
        console.log('Employees on leave:', onLeaveEmployees.map(e => e.name));
        return onLeaveEmployees.length;
      })(),
      
      // Rate metrics, all computed server-side (businessService.calculateRateMetrics) -
      // never duplicated here. Client BILL rates (by site, by hours) feed the AVG BILL
      // RATE tile; avgEmployeePay = company-wide pay across confirmed rates only.
      // (The legacy staff-pay avgBillableRate/billableEmployeeCount are no longer used here.)
      avgClientBillRateByHours: businessData.financialAnalysis?.avgClientBillRateByHours ?? null,
      avgClientBillRate: businessData.financialAnalysis?.avgClientBillRate ?? null,
      clientBillRateCount: businessData.financialAnalysis?.clientBillRateCount ?? 0,
      clientsWithoutRate: businessData.financialAnalysis?.clientsWithoutRate ?? [],
      avgEmployeePay: businessData.financialAnalysis?.avgEmployeePay ?? null,
      employeePayConfirmedCount: businessData.financialAnalysis?.employeePayConfirmedCount ?? 0,
      employeePayUnconfirmedCount: businessData.financialAnalysis?.employeePayUnconfirmedCount ?? 0,
      employeePayUnconfirmedNames: businessData.financialAnalysis?.employeePayUnconfirmedNames ?? [],
      
      // Business health - was gated on activeGuards.length (a role-name match for
      // "guard"), which is always 0 for companies whose roles are named e.g. "Driver"/
      // "employee"/"CDO" instead, forcing 'Unknown' regardless of actual profitability.
      // Now driven by the same profitMargin the tile's "change" label already uses
      // (businessData.financialAnalysis?.profitMargin), so value and change agree.
      businessHealth: (() => {
        const margin = businessData.financialAnalysis?.profitMargin;
        if (!derivedMetrics.monthlyRevenue || margin === null || margin === undefined) {
          return 'Unknown';
        }
        if (margin > 20) return 'Excellent';
        if (margin > 10) return 'Good';
        return 'Needs Attention';
      })(),
      
      // Profit margin comes straight from the backend's financialAnalysis (businessService.js) -
      // no longer recomputed here, so it can't drift from the zero-safe backend calculation.
      profitMargin: businessData.financialAnalysis?.profitMargin ?? 0,
      
      // Role breakdowns
      roleBreakdowns: {
        drivers: safeArrayFilter(businessData.employees, e => {
          const role = (e.role || '').toLowerCase();
          return e.status === 'Active' && (role.includes('patrol') || role.includes('driver') || role.includes('mobile'));
        }).length,
        staticGuards: derivedMetrics.activeGuards.length,
        supervisors: derivedMetrics.supervisors.length
      },
      
      // Client details
      clientDetails: businessData.clients?.map(client => ({
        name: client.name,
        revenue: client.monthly_revenue || 0,
        type: client.type || 'Unknown'
      })).sort((a, b) => b.revenue - a.revenue) || [],
      
      // Profit breakdown and gross profit both come from the backend's financialAnalysis -
      // same numbers businessService.js uses everywhere else (chat, financial analysis),
      // computed with a zero-safe hourly-rate fallback (safeFormatNumber only substitutes a
      // default for null/undefined/NaN, never for a legitimate $0 rate).
      profitBreakdown: businessData.financialAnalysis ? {
        totalRevenue: businessData.financialAnalysis.totalRevenue,
        totalLaborCosts: businessData.financialAnalysis.totalLaborCosts,
        grossProfit: businessData.financialAnalysis.grossProfit,
        profitMargin: businessData.financialAnalysis.profitMargin
      } : {
        totalRevenue: derivedMetrics.monthlyRevenue,
        totalLaborCosts: 0,
        grossProfit: derivedMetrics.monthlyRevenue,
        profitMargin: 0
      },

      grossProfit: businessData.financialAnalysis?.grossProfit ?? derivedMetrics.monthlyRevenue,

      // Net Profit = Gross Profit (staffed contracts only) - non-billable staff pay -
      // overhead. Also computed entirely server-side (businessService.calculateNetProfit) -
      // shown even when overhead isn't configured yet, clearly labeled as partial.
      netProfit: businessData.financialAnalysis?.netProfit ?? null,
      netProfitMargin: businessData.financialAnalysis?.netProfitMargin ?? null,
      overheadConfigured: businessData.financialAnalysis?.overheadConfigured ?? false,
      nonBillableStaffPay: businessData.financialAnalysis?.nonBillableStaffPay ?? 0,
      clientsNeedingStaffing: businessData.financialAnalysis?.clientsNeedingStaffing ?? [],
      employeesNeedingRateConfirmation: businessData.financialAnalysis?.employeesNeedingRateConfirmation ?? [],

      // Which method produced the overhead figure above - 'flat_expenses' | 'oer_derived' |
      // 'ambiguous_needs_choice' | 'not_configured'. Lets the UI always say where the
      // number came from instead of presenting it as if there were only one possible source.
      overheadMethod: businessData.financialAnalysis?.overheadMethod ?? 'not_configured',
      overheadAmount: businessData.financialAnalysis?.overheadAmount ?? 0,
      overheadAmbiguous: businessData.financialAnalysis?.overheadAmbiguous ?? false
    }
  };

  const refreshData = useCallback(async () => {
    console.log('Dashboard refresh triggered - fetching fresh data...');
    await fetchBusinessData();
  }, [fetchBusinessData]);

  return {
    businessData: analyticsData,  // Pass the enhanced data with analytics
    isLoading,
    error,
    refreshData,
    derivedMetrics: {
      ...derivedMetrics,
      totalModules: businessData.platformModules?.length || 3
    },
    // SAFE: Helper methods for components
    safeEmployees: Array.isArray(businessData.employees) ? businessData.employees : [],
    safeClients: Array.isArray(businessData.clients) ? businessData.clients : [],
    safeSites: Array.isArray(businessData.sites) ? businessData.sites : []
  };
};
